/**
 * TotalsTab Component
 *
 * Displays totals (over/under) market data including:
 * - Current total lines from all bookmakers
 * - Total line movement analysis
 * - Key number visualization for totals
 */

import type { BettingVolumeResponse } from '../../../api/client';

interface TotalsTabProps {
  bettingVolumeData: BettingVolumeResponse | null | undefined;
  homeTeam: string;
  awayTeam: string;
}

// Format decimal to American odds
const formatAmericanOdds = (price: number): string => {
  if (price >= 2) {
    return `+${Math.round((price - 1) * 100)}`;
  }
  return `-${Math.round(100 / (price - 1))}`;
};

export default function TotalsTab({
  bettingVolumeData,
  homeTeam,
  awayTeam,
}: TotalsTabProps) {
  // Find totals market data
  const totalsMarket = bettingVolumeData?.markets.find(m => m.marketType === 'totals');

  if (!totalsMarket) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">No totals data available</p>
        <p className="text-gray-500 text-sm mt-2">
          Over/Under data will appear when odds are collected from bookmakers
        </p>
      </div>
    );
  }

  // Group outcomes by total value
  const totalsByPoint = new Map<number, {
    overOdds: Array<{ bookmaker: string; price: number; isSharp: boolean }>;
    underOdds: Array<{ bookmaker: string; price: number; isSharp: boolean }>;
  }>();

  totalsMarket.outcomes.forEach(outcome => {
    const point = outcome.point ?? 0;
    const isOver = outcome.name.toLowerCase().includes('over');

    if (!totalsByPoint.has(point)) {
      totalsByPoint.set(point, { overOdds: [], underOdds: [] });
    }

    const entry = totalsByPoint.get(point)!;
    outcome.bookmakers.forEach(bookie => {
      const oddsEntry = { bookmaker: bookie.name, price: bookie.price, isSharp: bookie.isSharp };
      if (isOver) {
        entry.overOdds.push(oddsEntry);
      } else {
        entry.underOdds.push(oddsEntry);
      }
    });
  });

  // Get the main total line (most common)
  const mainTotal = Array.from(totalsByPoint.entries())
    .sort((a, b) => Math.max(b[1].overOdds.length, b[1].underOdds.length) - Math.max(a[1].overOdds.length, a[1].underOdds.length))[0];

  // Get all available totals sorted
  const sortedTotals = Array.from(totalsByPoint.entries()).sort((a, b) => a[0] - b[0]);

  return (
    <div className="space-y-6">
      {/* Main Total Display */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">OVER/UNDER</h2>
        <p className="text-sm text-gray-400 mb-4">
          {totalsMarket.bookmakerCount} bookmakers offering totals
        </p>

        {/* Current Total Summary */}
        {mainTotal && (
          <div className="bg-surface-50 rounded-lg p-6 border border-gray-800 mb-6">
            <div className="text-center mb-4">
              <span className="text-xs text-gray-500 uppercase">Main Line</span>
            </div>

            <div className="text-center mb-6">
              <div className="text-5xl font-bold font-mono text-white">
                {mainTotal[0]}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Combined Points ({homeTeam} + {awayTeam})
              </p>
            </div>

            <div className="grid grid-cols-2 gap-8">
              {/* Over */}
              <div className="text-center p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <span className="text-lg font-bold text-green-400">OVER</span>
                <div className="text-3xl font-bold font-mono mt-2 text-green-400">
                  {mainTotal[1].overOdds.length > 0
                    ? formatAmericanOdds(Math.max(...mainTotal[1].overOdds.map(o => o.price)))
                    : '-'}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {mainTotal[1].overOdds.length} books
                </div>
              </div>

              {/* Under */}
              <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                <span className="text-lg font-bold text-red-400">UNDER</span>
                <div className="text-3xl font-bold font-mono mt-2 text-red-400">
                  {mainTotal[1].underOdds.length > 0
                    ? formatAmericanOdds(Math.max(...mainTotal[1].underOdds.map(o => o.price)))
                    : '-'}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {mainTotal[1].underOdds.length} books
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alternative Totals */}
        {totalsByPoint.size > 1 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Alternative Totals
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {sortedTotals.map(([point, odds]) => {
                const isMain = mainTotal && point === mainTotal[0];

                return (
                  <div
                    key={point}
                    className={`p-3 rounded-lg border ${
                      isMain
                        ? 'bg-purple-500/10 border-purple-500/30'
                        : 'bg-surface-50 border-gray-800'
                    }`}
                  >
                    <div className="text-center mb-2">
                      <span className={`text-lg font-bold font-mono ${
                        isMain ? 'text-purple-400' : 'text-white'
                      }`}>
                        {point}
                      </span>
                      {isMain && (
                        <span className="ml-2 text-xs text-purple-400">Main</span>
                      )}
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-green-400">
                        O: {odds.overOdds.length > 0
                          ? formatAmericanOdds(Math.max(...odds.overOdds.map(o => o.price)))
                          : '-'}
                      </span>
                      <span className="text-red-400">
                        U: {odds.underOdds.length > 0
                          ? formatAmericanOdds(Math.max(...odds.underOdds.map(o => o.price)))
                          : '-'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bookmaker Breakdown Table */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">TOTALS BY BOOKMAKER</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-700">
                <th className="pb-3 pr-4">Bookmaker</th>
                <th className="pb-3 px-4 text-center">Total</th>
                <th className="pb-3 px-4 text-center">Over</th>
                <th className="pb-3 px-4 text-center">Under</th>
                <th className="pb-3 pl-4">Type</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Build bookmaker map
                const bookmakers = new Map<string, {
                  total?: number;
                  overPrice?: number;
                  underPrice?: number;
                  isSharp: boolean;
                }>();

                totalsMarket.outcomes.forEach(outcome => {
                  const point = outcome.point ?? 0;
                  const isOver = outcome.name.toLowerCase().includes('over');

                  outcome.bookmakers.forEach(bookie => {
                    if (!bookmakers.has(bookie.name)) {
                      bookmakers.set(bookie.name, { isSharp: bookie.isSharp });
                    }
                    const entry = bookmakers.get(bookie.name)!;
                    entry.total = point;
                    if (isOver) {
                      entry.overPrice = bookie.price;
                    } else {
                      entry.underPrice = bookie.price;
                    }
                  });
                });

                return Array.from(bookmakers.entries())
                  .sort((a, b) => (b[1].isSharp ? 1 : 0) - (a[1].isSharp ? 1 : 0))
                  .map(([name, data]) => (
                    <tr key={name} className="border-b border-gray-800 hover:bg-surface-50">
                      <td className="py-3 pr-4 font-medium">{name}</td>
                      <td className="py-3 px-4 text-center font-mono text-white">
                        {data.total ?? '-'}
                      </td>
                      <td className="py-3 px-4 text-center font-mono text-green-400">
                        {data.overPrice ? formatAmericanOdds(data.overPrice) : '-'}
                      </td>
                      <td className="py-3 px-4 text-center font-mono text-red-400">
                        {data.underPrice ? formatAmericanOdds(data.underPrice) : '-'}
                      </td>
                      <td className="py-3 pl-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          data.isSharp
                            ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                            : 'bg-gray-700/50 text-gray-400'
                        }`}>
                          {data.isSharp ? 'Sharp' : 'Square'}
                        </span>
                      </td>
                    </tr>
                  ));
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals Range Visualization */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">TOTALS RANGE</h2>
        <p className="text-sm text-gray-400 mb-4">
          Available total lines across bookmakers
        </p>

        {sortedTotals.length > 0 && (
          <div className="relative h-16">
            {/* Range bar */}
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-2 bg-gray-700 rounded-full">
              {/* Available range highlight */}
              <div
                className="absolute h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-full opacity-60"
                style={{
                  left: '10%',
                  right: '10%',
                }}
              />
            </div>

            {/* Point markers */}
            {sortedTotals.map(([point, _odds], idx) => {
              const isMain = mainTotal && point === mainTotal[0];
              const position = 10 + (80 * idx) / Math.max(sortedTotals.length - 1, 1);

              return (
                <div
                  key={point}
                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${position}%` }}
                >
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    isMain
                      ? 'bg-purple-500 border-purple-300'
                      : 'bg-gray-600 border-gray-500'
                  }`} />
                  <div className={`absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono ${
                    isMain ? 'text-purple-400 font-bold' : 'text-gray-500'
                  }`}>
                    {point}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 flex justify-between text-xs text-gray-500">
          <span>Lower Scoring</span>
          <span>Higher Scoring</span>
        </div>
      </div>
    </div>
  );
}
